/**
 * The basket - the panel on the right of the POS screen.
 *
 * This slice holds what the cashier has tapped and what it *appears* to cost.
 * The server recomputes every figure when the order is saved, and the response
 * replaces what is here. That is deliberate:
 *
 *   - the numbers on screen have to update on the same frame as the tap, or the
 *     till feels broken. A round trip per item does not do that.
 *   - the numbers that end up on the receipt and in the tax return must come
 *     from the database, where prices and rates actually live.
 *
 * So the arithmetic below is a preview. It matches the server's rules (see
 * server/src/services/taxService.js) closely enough that the figure rarely
 * changes on save, but it is never the source of truth.
 */

import { createSlice } from '@reduxjs/toolkit';

/** Two decimal rounding, matching the server's money() helper. */
const money = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return 0;
    return Math.round((num + Number.EPSILON) * 100) / 100;
};

/**
 * Two taps of the same item should stack into one line - but only if every
 * choice matches. A Zinger with extra cheese is a different line from a plain
 * one, and merging them would misreport the kitchen ticket.
 *
 * A line already sent to the kitchen keeps its own key, so ordering the same
 * dish again adds a second line rather than growing the first. That is both
 * what a restaurant means by a second round, and what stops the quantity of a
 * row the kitchen is already cooking from changing underneath them.
 */
const lineKey = (line) =>
    [
        line.menuItemId,
        line.variantId ?? 'x',
        (line.modifiers || []).map((m) => m.modifierId).sort().join('.'),
        line.itemNote || '',
        line.unitRate,
        line.orderItemId ?? 'new'
    ].join('|');

/** Price one line the way the server does: tax lives inside the price. */
const priceLine = (line) => {
    const modifierAmount = money(
        (line.modifiers || []).reduce((t, m) => t + (m.price || 0) * (m.quantity || 1), 0)
    );

    const perUnit = money(line.unitRate + modifierAmount);
    const grossAmount = money(perUnit * line.quantity);
    const discountAmount = line.discountAmount != null
        ? money(Math.min(line.discountAmount, grossAmount))
        : money(grossAmount * (line.discountPercent || 0) / 100);

    const netAmount = money(grossAmount - discountAmount);
    const taxPercent = line.taxPercent || 0;

    // Tax-inclusive: back it out rather than adding it on.
    const valueExcludingTax = line.priceIncludesTax === false
        ? netAmount
        : money(netAmount / (1 + taxPercent / 100));

    const taxAmount = line.priceIncludesTax === false
        ? money(netAmount * taxPercent / 100)
        : money(netAmount - valueExcludingTax);

    const lineTotal = line.priceIncludesTax === false
        ? money(netAmount + taxAmount)
        : netAmount;

    return { ...line, modifierAmount, grossAmount, discountAmount, valueExcludingTax, taxAmount, lineTotal };
};

const initialState = {
    // The saved order this basket belongs to, once it has been saved at least
    // once. Null while the ticket is still only on screen.
    orderId: null,
    orderNumber: null,

    orderTypeCode: 'DINE_IN',
    tableId: null,
    tableNumber: null,
    customerId: null,
    customerName: 'Walk-in Customer',
    customerMobile: null,
    customerAddressId: null,
    waiterEmployeeId: null,
    guestCount: 1,
    orderNote: '',

    lines: [],

    discountTypeId: null,
    discountPercent: 0,
    discountAmount: null,
    serviceChargePercent: 0,
    deliveryCharge: 0,

    // Set from the server response so the panel shows what was actually stored
    // rather than the preview.
    serverTotals: null,
    isDirty: false,
    isSaving: false
};

const cartSlice = createSlice({
    name: 'cart',
    initialState,
    reducers: {
        addItem: (state, action) => {
            const {
                menuItemId, variantId = null, itemName, variantName = null,
                unitRate, taxPercent = 0, priceIncludesTax = true,
                modifiers = [], quantity = 1, itemNote = null,
                maxDiscountPercent = 100, uom = null, kitchenSection = null,
                isDeal = false, imagePath = null
            } = action.payload;

            const candidate = {
                menuItemId, variantId, itemName, variantName,
                unitRate: money(unitRate), taxPercent, priceIncludesTax,
                modifiers, quantity, itemNote, maxDiscountPercent,
                uom, kitchenSection, isDeal, imagePath,
                discountPercent: 0, discountAmount: null
            };

            const key = lineKey(candidate);
            const existing = state.lines.find((l) => l.key === key);

            if (existing) {
                existing.quantity = money(existing.quantity + quantity);
                Object.assign(existing, priceLine(existing));
            } else {
                state.lines.push({ key, ...priceLine(candidate) });
            }

            state.isDirty = true;
        },

        setLineQuantity: (state, action) => {
            const { key, quantity } = action.payload;
            const line = state.lines.find((l) => l.key === key);
            if (!line) return;

            if (quantity <= 0) {
                state.lines = state.lines.filter((l) => l.key !== key);
            } else {
                line.quantity = quantity;
                Object.assign(line, priceLine(line));
            }
            state.isDirty = true;
        },

        incrementLine: (state, action) => {
            const line = state.lines.find((l) => l.key === action.payload);
            if (!line) return;
            line.quantity = money(line.quantity + 1);
            Object.assign(line, priceLine(line));
            state.isDirty = true;
        },

        decrementLine: (state, action) => {
            const line = state.lines.find((l) => l.key === action.payload);
            if (!line) return;

            if (line.quantity <= 1) {
                state.lines = state.lines.filter((l) => l.key !== action.payload);
            } else {
                line.quantity = money(line.quantity - 1);
                Object.assign(line, priceLine(line));
            }
            state.isDirty = true;
        },

        removeLine: (state, action) => {
            state.lines = state.lines.filter((l) => l.key !== action.payload);
            state.isDirty = true;
        },

        setLineDiscount: (state, action) => {
            const { key, discountPercent, discountAmount } = action.payload;
            const line = state.lines.find((l) => l.key === key);
            if (!line) return;

            // The item's own ceiling is enforced here as well as on the server,
            // so the cashier is told immediately rather than at save time.
            const capped = Math.min(discountPercent ?? 0, line.maxDiscountPercent ?? 100);
            line.discountPercent = capped;
            line.discountAmount = discountAmount ?? null;
            Object.assign(line, priceLine(line));
            state.isDirty = true;
        },

        setLineNote: (state, action) => {
            const { key, itemNote } = action.payload;
            const line = state.lines.find((l) => l.key === key);
            if (!line) return;
            line.itemNote = itemNote;
            // The note is part of the identity of a line, so the key moves with it.
            line.key = lineKey(line);
            state.isDirty = true;
        },

        setOrderType: (state, action) => {
            state.orderTypeCode = action.payload;
            // A table only means something for dine-in.
            if (action.payload !== 'DINE_IN') {
                state.tableId = null;
                state.tableNumber = null;
            }
            state.isDirty = true;
        },

        setTable: (state, action) => {
            state.tableId = action.payload?.tableId ?? null;
            state.tableNumber = action.payload?.tableNumber ?? null;
            state.isDirty = true;
        },

        setCustomer: (state, action) => {
            state.customerId = action.payload?.customerId ?? null;
            state.customerName = action.payload?.customerName ?? 'Walk-in Customer';
            state.customerMobile = action.payload?.mobileNo ?? null;
            state.customerAddressId = action.payload?.customerAddressId ?? null;
            state.isDirty = true;
        },

        setWaiter: (state, action) => {
            state.waiterEmployeeId = action.payload;
            state.isDirty = true;
        },

        setGuestCount: (state, action) => {
            state.guestCount = Math.max(1, action.payload);
            state.isDirty = true;
        },

        setOrderNote: (state, action) => {
            state.orderNote = action.payload;
            state.isDirty = true;
        },

        setOrderDiscount: (state, action) => {
            const { discountTypeId, discountPercent, discountAmount } = action.payload;
            state.discountTypeId = discountTypeId ?? null;
            state.discountPercent = discountPercent ?? 0;
            state.discountAmount = discountAmount ?? null;
            state.isDirty = true;
        },

        setDeliveryCharge: (state, action) => {
            state.deliveryCharge = money(action.payload);
            state.isDirty = true;
        },

        setSaving: (state, action) => { state.isSaving = action.payload; },

        /**
         * Replace the basket with what the server stored.
         * Called after every save, so the panel shows the authoritative figures
         * rather than the preview.
         */
        loadFromOrder: (state, action) => {
            const { order, items } = action.payload;

            state.orderId = order.Order_ID;
            state.orderNumber = order.Order_Number;
            state.orderTypeCode = order.Order_Type_Code;
            state.tableId = order.Table_ID;
            state.tableNumber = order.Table_Number;
            state.customerId = order.Customer_ID;
            state.customerName = order.Customer_Name || 'Walk-in Customer';
            state.customerMobile = order.Customer_Mobile;
            state.customerAddressId = order.Customer_Address_ID;
            state.waiterEmployeeId = order.Waiter_Employee_ID;
            state.guestCount = order.Guest_Count || 1;
            state.orderNote = order.Order_Note || '';
            state.discountTypeId = order.Discount_Type_ID;
            state.discountPercent = Number(order.Discount_Percent) || 0;
            state.discountAmount = null;
            state.serviceChargePercent = Number(order.Service_Charge_Percent) || 0;
            state.deliveryCharge = Number(order.Delivery_Charge) || 0;

            state.lines = (items || [])
                .filter((item) => !item.Is_Voided)
                .map((item) => ({
                    key: `srv-${item.Order_Item_ID}`,
                    orderItemId: item.Order_Item_ID,
                    menuItemId: item.Menu_Item_ID,
                    variantId: item.Variant_ID,
                    itemName: item.Item_Name,
                    variantName: item.Variant_Name,
                    quantity: Number(item.Quantity),
                    unitRate: Number(item.Unit_Rate),
                    modifierAmount: Number(item.Modifier_Amount),
                    taxPercent: Number(item.Tax_Percent),
                    priceIncludesTax: true,
                    discountPercent: Number(item.Discount_Percent),
                    discountAmount: Number(item.Discount_Amount) || null,
                    grossAmount: Number(item.Gross_Amount),
                    valueExcludingTax: Number(item.Value_Excluding_Tax),
                    taxAmount: Number(item.Tax_Amount),
                    lineTotal: Number(item.Line_Total),
                    itemNote: item.Item_Note,
                    uom: item.UOM_Name,
                    // Lines already sent to the kitchen cannot be edited away;
                    // the food is being cooked.
                    isSentToKitchen: item.KOT_Status !== 'PENDING',
                    modifiers: (item.modifiers || []).map((m) => ({
                        modifierId: m.Modifier_ID,
                        name: m.Modifier_Name,
                        price: Number(m.Additional_Price),
                        quantity: Number(m.Quantity)
                    }))
                }));

            state.serverTotals = {
                grossAmount: Number(order.Gross_Amount),
                itemDiscount: Number(order.Item_Discount),
                orderDiscount: Number(order.Order_Discount),
                subTotal: Number(order.Sub_Total),
                serviceCharge: Number(order.Service_Charge),
                deliveryCharge: Number(order.Delivery_Charge),
                taxAmount: Number(order.Tax_Amount),
                posServiceFee: Number(order.POS_Service_Fee),
                roundOff: Number(order.Round_Off),
                totalPayable: Number(order.Total_Payable)
            };

            state.isDirty = false;
            state.isSaving = false;
        },

        /** Start a fresh ticket. */
        clearCart: () => ({ ...initialState })
    }
});

export const {
    addItem, setLineQuantity, incrementLine, decrementLine, removeLine,
    setLineDiscount, setLineNote,
    setOrderType, setTable, setCustomer, setWaiter, setGuestCount, setOrderNote,
    setOrderDiscount, setDeliveryCharge, setSaving, loadFromOrder, clearCart
} = cartSlice.actions;

/* -------------------------------- selectors ------------------------------- */

export const selectCart = (state) => state.cart;
export const selectLines = (state) => state.cart.lines;
export const selectLineCount = (state) => state.cart.lines.length;

/**
 * What the panel shows.
 *
 * Prefers the server's figures when the basket is unchanged since the last
 * save; falls back to the local preview the moment the cashier taps something,
 * so the total never lags behind the screen.
 */
export const selectTotals = (state) => {
    const { lines, serverTotals, isDirty, discountPercent, discountAmount,
        serviceChargePercent, deliveryCharge } = state.cart;

    if (serverTotals && !isDirty) return serverTotals;

    // The preview has to add up the same way the server does, or the figure on
    // the Pay button jumps when the order is saved.
    const settings = state.menu?.settings || {};
    const pricesIncludeTax = settings.PRICES_INCLUDE_TAX === true;
    const posServiceFee = settings.CHARGE_SERVICE_FEE === false
        ? 0
        : Number(settings.POS_SERVICE_FEE || 0);
    const roundOffTo = Number(settings.ROUND_OFF_TO ?? 1);

    const grossAmount = money(lines.reduce((t, l) => t + l.grossAmount, 0));
    const itemDiscount = money(lines.reduce((t, l) => t + l.discountAmount, 0));
    const afterItemDiscount = money(grossAmount - itemDiscount);

    const orderDiscount = discountAmount != null
        ? money(Math.min(discountAmount, afterItemDiscount))
        : money(afterItemDiscount * discountPercent / 100);

    const subTotal = money(afterItemDiscount - orderDiscount);

    // Scale the line tax by what survived the order discount, as the server does.
    const ratio = afterItemDiscount > 0 ? subTotal / afterItemDiscount : 1;
    const taxAmount = money(lines.reduce((t, l) => t + l.taxAmount * ratio, 0));

    const serviceCharge = money(subTotal * (serviceChargePercent || 0) / 100);

    // Exclusive pricing adds the tax on top; inclusive pricing already has it
    // inside the sub total and adding it again would charge it twice.
    const beforeRounding = pricesIncludeTax
        ? money(subTotal + serviceCharge + (deliveryCharge || 0) + posServiceFee)
        : money(subTotal + taxAmount + serviceCharge + (deliveryCharge || 0) + posServiceFee);

    const totalPayable = roundOffTo > 0
        ? money(Math.round(beforeRounding / roundOffTo) * roundOffTo)
        : beforeRounding;

    return {
        grossAmount,
        itemDiscount,
        orderDiscount,
        subTotal,
        serviceCharge,
        deliveryCharge: money(deliveryCharge),
        taxAmount,
        posServiceFee,
        roundOff: money(totalPayable - beforeRounding),
        totalPayable
    };
};

/** The payload the server expects when the basket is saved. */
export const selectOrderPayload = (state) => {
    const cart = state.cart;
    return {
        orderId: cart.orderId,
        orderTypeCode: cart.orderTypeCode,
        tableId: cart.tableId,
        customerId: cart.customerId,
        customerAddressId: cart.customerAddressId,
        waiterEmployeeId: cart.waiterEmployeeId,
        guestCount: cart.guestCount,
        orderNote: cart.orderNote || null,
        discountTypeId: cart.discountTypeId,
        discountPercent: cart.discountPercent,
        discountAmount: cart.discountAmount,
        deliveryCharge: cart.deliveryCharge,
        items: cart.lines.map((line) => ({
            // Carried for lines that came back from the server, so a re-save
            // updates them instead of inserting a duplicate.
            orderItemId: line.orderItemId ?? null,
            menuItemId: line.menuItemId,
            variantId: line.variantId,
            quantity: line.quantity,
            unitRate: line.unitRate,
            discountPercent: line.discountPercent,
            discountAmount: line.discountAmount,
            itemNote: line.itemNote,
            modifiers: (line.modifiers || []).map((m) => ({
                modifierId: m.modifierId,
                quantity: m.quantity || 1
            }))
        }))
    };
};

export default cartSlice.reducer;
